import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DocumentList } from '@/components/documents/DocumentList';
import { FileUpload } from '@/components/documents/FileUpload';
import { TaxFilingsPanel } from '@/components/tax-filings/TaxFilingsPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminScope } from '@/contexts/AdminScopeContext';
import { getFullName } from '@/lib/utils';
import {
  deleteDocument,
  downloadDocument,
  getDocuments,
  uploadDocumentsOnBehalfOfUser,
} from '@/services/documentApi';
import { getUserById } from '@/services/userApi';
import { Document, UploadProgress } from '@/types/document';
import { TAX_FILINGS_TAB, TaxFilingsTab } from '@/types/taxFilings';
import { UserListItem } from '@/types/user';
import { ArrowLeft, FileEdit, Upload, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface FileWithId extends File {
  id: string;
}

interface UploadProgressState {
  id: string;
  file: FileWithId;
  progress: number;
  status: UploadProgress['status'];
  error?: string;
}

const VALID_FILING_TABS = new Set<string>(Object.values(TAX_FILINGS_TAB));

function parseFilingTab(raw?: string): TaxFilingsTab {
  if (raw && VALID_FILING_TABS.has(raw)) return raw as TaxFilingsTab;
  return TAX_FILINGS_TAB.TAX_SUMMARY;
}

export default function UserDetailPage() {
  const { userId, filingTab: filingTabParam } = useParams<{
    userId: string;
    filingTab?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedYear, selectedTenantId } = useAdminScope();
  const [user, setUser] = useState<UserListItem | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressState[]>([]);

  const section = location.pathname.includes('/tax-filings') ? 'tax-filings' : 'documents';
  const filingTab = useMemo(() => parseFilingTab(filingTabParam), [filingTabParam]);
  const encodedUserId = userId ? encodeURIComponent(userId) : '';

  useEffect(() => {
    if (section === 'tax-filings' && filingTabParam && !VALID_FILING_TABS.has(filingTabParam) && userId) {
      navigate(`/admin/users/${encodedUserId}/tax-filings/tax-summary`, { replace: true });
    }
  }, [section, filingTabParam, userId, encodedUserId, navigate]);

  useEffect(() => {
    if (userId) {
      void loadData();
    }
  }, [userId, selectedYear, selectedTenantId]);

  const loadData = async () => {
    if (!userId) return;

    try {
      const [userData, docsData] = await Promise.all([
        getUserById(userId, selectedTenantId),
        getDocuments({
          userId,
          taxYear: selectedYear,
          tenantId: selectedTenantId,
        }),
      ]);
      setUser(userData);
      setDocuments(docsData);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDoc = (doc: Document) => {
    toast.info(`Viewing: ${doc.originalName}`);
  };

  const handleDownload = async (doc: Document) => {
    try {
      const blob = await downloadDocument(
        doc.id,
        userId ?? undefined,
        selectedTenantId
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Downloaded: ${doc.originalName}`);
    } catch {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteClick = (doc: Document) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    setIsDeleting(true);
    try {
      await deleteDocument(documentToDelete.id);
      setDocuments((prev) => prev.filter((d) => d.id !== documentToDelete.id));
      toast.success(`Deleted: ${documentToDelete.originalName}`);
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch {
      toast.error('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleAddDocumentFilesSelected = () => setUploadProgress([]);

  const handleAddDocumentUpload = async (files: File[]) => {
    if (!user || !userId) return;
    setIsUploadingDocs(true);
    const filesWithId = files as FileWithId[];
    setUploadProgress(
      filesWithId.map((file) => ({
        id: file.id,
        file,
        progress: 0,
        status: 'pending' as const,
      }))
    );
    try {
      const name = getFullName(user);
      await uploadDocumentsOnBehalfOfUser(
        user.email,
        name,
        files,
        (fileIndex, progress, status) => {
          setUploadProgress((prev) => {
            const next = [...prev];
            if (next[fileIndex]) next[fileIndex] = { ...next[fileIndex], progress, status };
            return next;
          });
        },
        { taxYear: selectedYear, tenantId: selectedTenantId }
      );
      toast.success(
        `Uploaded ${files.length} document${files.length !== 1 ? 's' : ''} for ${name}`
      );
      await loadData();
      setAddDocumentDialogOpen(false);
      setUploadProgress([]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload documents');
    } finally {
      setIsUploadingDocs(false);
    }
  };

  const handleSectionChange = (value: string) => {
    if (!userId) return;
    if (value === 'documents') {
      navigate(`/admin/users/${encodedUserId}/documents`);
    } else {
      navigate(`/admin/users/${encodedUserId}/tax-filings/tax-summary`);
    }
  };

  const handleFilingTabChange = (tab: TaxFilingsTab) => {
    if (!userId) return;
    navigate(`/admin/users/${encodedUserId}/tax-filings/${tab}`, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Skeleton className="h-32" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">User not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
          Back to Users
        </Button>
      </div>
    );
  }

  const userName = getFullName(user);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="h-5 w-5" />
            <span className="sr-only">Back to users</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{userName}</h1>
            <p className="text-muted-foreground">
              View and manage documents and tax filings for this user (selected year & tenant)
            </p>
          </div>
        </div>
        {userId && (
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => navigate(`/admin/users/${encodedUserId}/tax-form`)}
          >
            <FileEdit className="mr-2 h-4 w-4" />
            Fill tax forms
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{userName}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              {user.referralCode && (
                <p className="text-xs text-muted-foreground mt-1">
                  Referral code: {user.referralCode}
                </p>
              )}
            </div>
            <div className="text-right">
              <Badge variant={user.status === 'active' ? 'success' : 'secondary'}>
                {user.status}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">
                Joined {formatDate(user.createdAt)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={section} onValueChange={handleSectionChange} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="tax-filings">Tax Filings</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold">Documents ({documents.length})</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddDocumentDialogOpen(true)}
              className="shrink-0"
            >
              <Upload className="mr-2 h-4 w-4" />
              Add document
            </Button>
          </div>
          <DocumentList
            documents={documents}
            onView={handleViewDoc}
            onDownload={handleDownload}
            onDelete={handleDeleteClick}
            emptyTitle="No documents"
            emptyDescription="This user hasn't uploaded any documents yet."
          />
        </TabsContent>

        <TabsContent value="tax-filings" className="space-y-4">
          {userId && (
            <TaxFilingsPanel
              mode="admin"
              taxYear={selectedYear}
              userId={userId}
              tenantId={selectedTenantId}
              defaultTab={filingTab}
              onTabChange={handleFilingTabChange}
            />
          )}
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Document"
        description={`Are you sure you want to delete "${documentToDelete?.originalName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        loading={isDeleting}
      />

      <Dialog open={addDocumentDialogOpen} onOpenChange={setAddDocumentDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add document for {userName}</DialogTitle>
            <DialogDescription>
              Upload one or more documents on behalf of this user.
            </DialogDescription>
          </DialogHeader>
          <FileUpload
            onFilesSelected={handleAddDocumentFilesSelected}
            onUpload={handleAddDocumentUpload}
            maxFiles={60}
            minFiles={1}
            isUploading={isUploadingDocs}
            uploadProgress={uploadProgress}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
